import { LitElement, html, css } from './-global.min.js'

class HHBannerVideo extends LitElement {
  static properties = {
    overlayTransparency: {
      type: Number,
    },
    enableSound: {
      type: Boolean,
      attribute: 'enable-sound',
    },
    isPlaying: {
      type: Boolean,
      state: true,
      reflect: true,
    },
    isMuted: {
      type: Boolean,
      state: true,
      reflect: true,
    },
  }

  constructor() {
    super()
    this.isPlaying = true
    this.isMuted = true
    this.enableSound = false
    this.overlayTransparency = 0.5
  }

  connectedCallback() {
    super.connectedCallback()
  }

  onClick(e) {
    const videoEl = this.querySelector('video')
    const isPaused = videoEl.paused
    isPaused ? videoEl.play() : videoEl.pause()

    this.isPlaying = isPaused
  }

  onSoundClick(e) {
    const videoEl = this.querySelector('video')
    videoEl.muted = !videoEl.muted

    this.isMuted = videoEl.muted
  }

  static styles = css`
    :host([theme='light']) {
      color: var(--hh-color-reverse);
    }

    :host([theme='dark']) {
      color: var(--hh-color-ink);
    }

    :host .hh-banner-video {
      height: 100%;
      position: relative;
      width: 100%;
    }

    .hh-banner-video__overlay {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      top: 0;
      left: 0;
      right: 0;
      background: var(--banner-video-overlay);
    }

    :host(.overlay-custom-bg) .hh-banner-video__overlay {
      background: var(--hh-overlay-bg);
    }

    .hh-banner-video__controls-container {
      position: absolute;
      display: flex;
      align-items: center;
      gap: 12px;
      bottom: 19px;
      right: 19px;
      z-index: 5;
    }

    .hh-banner-video__controls-btn {
      padding: 0;
      border: none;
      background: none;
      color: inherit;
      cursor: pointer;
    }

    .hh-banner-video__controls-btn[data-state='playing'] .hh-banner__play-icon,
    .hh-banner-video__controls-btn[data-state='paused'] .hh-banner__pause-icon {
      display: none;
    }

    .hh-banner-video__controls-btn[data-state='unmuted'] .hh-banner__unmute-icon,
    .hh-banner-video__controls-btn[data-state='muted'] .hh-banner__mute-icon {
      display: none;
    }
  `

  render() {
    return html`
      <div class="hh-banner-video">
        <div class="hh-banner-video__overlay" aria-hidden="true"></div>
        <slot></slot>
        <div class="hh-banner-video__controls-container">
          ${this.enableSound
            ? html`
                <button
                  type="button"
                  class="hh-banner-video__controls-btn"
                  aria-label="Toggle sound"
                  @click="${this.onSoundClick}"
                  data-state="${this.isMuted ? 'muted' : 'unmuted'}"
                >
                  <div class="hh-banner__mute-icon" aria-hidden="true">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 512 512"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fill="currentColor" transform="translate(42.666667, 85.333333)">
                        <path
                          d="M361.299413,341.610667 L328.014293,314.98176 C402.206933,233.906133 402.206933,109.96608 328.013013,28.8906667 L361.298133,2.26304 C447.910187,98.97536 447.908907,244.898347 361.299413,341.610667 Z M276.912853,69.77216 L243.588693,96.4309333 C283.38432,138.998613 283.38304,204.87488 243.589973,247.44256 L276.914133,274.101333 C329.118507,215.880107 329.118507,127.992107 276.912853,69.77216 Z M191.749973,1.42108547e-14 L80.8957867,87.2292267 L7.10542736e-15,87.2292267 L7.10542736e-15,257.895893 L81.0208,257.895893 L191.749973,343.35424 L191.749973,1.42108547e-14 L191.749973,1.42108547e-14 Z"
                        />
                      </g>
                    </svg>
                  </div>
                  <div class="hh-banner__unmute-icon" aria-hidden="true">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 512 512"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fill="currentColor" transform="translate(42.666667, 59.581722)">
                        <path
                          d="M47.0849493,-1.42108547e-14 L298.668,251.583611 L304.101001,257.015597 L304.101,257.016 L353.573532,306.488791 C353.573732,306.488458 353.573933,306.488124 353.574133,306.48779 L384.435257,337.348961 L384.434,337.349 L409.751616,362.666662 L379.581717,392.836561 L191.749,205.003 L191.749973,369.105851 L81.0208,283.647505 L7.10542736e-15,283.647505 L7.10542736e-15,112.980838 L80.8957867,112.980838 L91.433,104.688 L16.9150553,30.169894 L47.0849493,-1.42108547e-14 Z M361.298133,28.0146513 C429.037729,103.653701 443.797162,209.394226 405.578884,298.151284 L372.628394,265.201173 C396.498256,194.197542 381.626623,113.228555 328.013013,54.642278 L361.298133,28.0146513 Z M276.912853,95.5237713 C305.539387,127.448193 318.4688,168.293162 315.701304,208.275874 L266.464558,159.040303 C261.641821,146.125608 254.316511,133.919279 244.488548,123.156461 L243.588693,122.182545 L276.912853,95.5237713 Z M191.749973,25.7516113 L191.749,84.3256113 L158.969,51.5456113 L191.749973,25.7516113 Z"
                        />
                      </g>
                    </svg>
                  </div>
                </button>
              `
            : html``}
          <button
            type="button"
            class="hh-banner-video__controls-btn"
            aria-label="Toggle video"
            @click="${this.onClick}"
            data-state="${this.isPlaying ? 'playing' : 'paused'}"
          >
            <div class="hh-banner__pause-icon" aria-hidden="true">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.00003 24.2002L7 2.2002"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  stroke-linecap="square"
                />
                <path
                  d="M17 24.2002L17 2.2002"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  stroke-linecap="square"
                />
              </svg>
            </div>
            <div class="hh-banner__play-icon" aria-hidden="true">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3,22.0000002 L21,12 L3,2 L3,22.0000002 Z M5,19 L17.5999998,11.9999999 L5,5 L5,19 Z M7,16 L14.1999999,12 L7,8 L7,16 Z M9,13 L10.8,12 L9,11 L9,13 Z"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    `
  }
}

customElements.define('hh-banner-video', HHBannerVideo)
